import type {
	ActivateProjectsResponse,
	DeactivateProjectsResponse,
	DeleteProjectsResponse,
	Project,
	ProjectFilters,
	ProjectsResponse,
} from '@/types/project'
import { ownerApiCall } from './axiosInstance'

function transformTenantToProject(tenant: {
	id: string
	name: string
	created_at: string
	created_by: string
	deactivated_at: string | null
	deactivated_by: string | null
	is_org?: boolean
	frontend_settings?: Record<string, unknown>
}): Project {
	return {
		id: tenant.id,
		name: tenant.name || tenant.id,
		status: tenant.deactivated_at ? 'deactivated' : 'active',
		users: 0, // Backend doesn't return this
		dataSizeClickhouse: '0 GB', // Backend doesn't return this
		dataSizeLake: '0 GB', // Backend doesn't return this
		createdAt: tenant.created_at,
		updatedAt: undefined,
		is_org: tenant.is_org,
		frontend_settings: tenant.frontend_settings,
	}
}

type ProjectAPIResponse = {
	id: string
	name: string
	created_at: string
	created_by: string
	deactivated_at: string | null
	deactivated_by: string | null
	is_org?: boolean
	frontend_settings?: Record<string, unknown>
}

// Shared helper for patching tenant settings
// Uses ownerApiCall to avoid the interceptor overwriting our x-bytebeam-tenant header
async function patchTenantSettings(tenantId: string, data: Record<string, unknown>): Promise<void> {
	await ownerApiCall('/v3/tenant-settings', {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
			'x-bytebeam-tenant': tenantId,
		},
		data,
	})
}

export const projectsApi = {
	listProjects: async (
		organizationId: string,
		filters: ProjectFilters = {},
		page = 1,
		limit = 10,
		signal?: AbortSignal,
	): Promise<ProjectsResponse> => {
		const response = await ownerApiCall<{
			tenants: ProjectAPIResponse[]
		}>(`/v3/organizations/${encodeURIComponent(organizationId)}/tenants`, { signal })

		let tenants = response.tenants.map(transformTenantToProject)

		// Apply filters (client-side since backend doesn't support them)
		if (filters.search) {
			const searchLower = filters.search.toLowerCase()
			tenants = tenants.filter(
				t => t.name.toLowerCase().includes(searchLower) || t.id.toLowerCase().includes(searchLower),
			)
		}
		if (filters.status) {
			tenants = tenants.filter(t => t.status === filters.status)
		}
		if (filters.name && filters.name.length > 0) {
			const names = filters.name
			tenants = tenants.filter(t => names.includes(t.name))
		}

		if (filters.sortBy) {
			tenants.sort((a, b) => {
				const aVal = a[filters.sortBy as keyof Project] as string | number
				const bVal = b[filters.sortBy as keyof Project] as string | number
				const comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
				return filters.sortOrder === 'desc' ? -comparison : comparison
			})
		}

		// Apply pagination
		const total = tenants.length
		const startIndex = (page - 1) * limit
		const endIndex = startIndex + limit
		const paginatedTenants = tenants.slice(startIndex, endIndex)

		return {
			data: paginatedTenants,
			allData: tenants,
			pagination: {
				page,
				limit,
				total,
				totalPages: Math.ceil(total / limit),
			},
		}
	},
	getProjectDetails: async (organizationId: string, projectId: string): Promise<Project> => {
		const response = await ownerApiCall<{
			tenants: ProjectAPIResponse[]
		}>(`/v3/organizations/${encodeURIComponent(organizationId)}/tenants`)

		const tenant = response.tenants.find(t => t.id === projectId)
		if (!tenant) {
			throw new Error(`Project with ID ${projectId} not found`)
		}

		return transformTenantToProject(tenant)
	},
	createProject: async (
		organizationId: string,
		tenantName: string,
		frontend_settings?: Record<string, unknown>,
	): Promise<{ id: string }> => {
		const template = 'Empty' // Backend support 3 templates: Empty, ByteOne, Uplink
		const response = await ownerApiCall<{ id: string }>(
			`/v3/organizations/${encodeURIComponent(organizationId)}/tenants`,
			{
				method: 'POST',
				data: {
					name: tenantName,
					template,
					...(frontend_settings ? { frontend_settings } : {}),
				},
			},
		)
		return response
	},
	// Note: Backend update support might be limited; returning partial object for store sync.
	updateProject: async (_organizationId: string, tenantId: string, name: string): Promise<Project> => {
		await patchTenantSettings(tenantId, { name })

		// Return a partial project object to satisfy the interface since backend returns { status: 'ok' }
		// The store will refetch the updated list anyway
		return {
			id: tenantId,
			name: name,
			status: 'active',
			users: 0,
			dataSizeClickhouse: '0 GB',
			dataSizeLake: '0 GB',
			createdAt: new Date().toISOString(),
		}
	},

	// Note: Backend currently supports single tenant activation; looping for bulk action.
	activateProjects: async (organizationId: string, projectIds: string[]): Promise<ActivateProjectsResponse> => {
		let activatedCount = 0
		for (const tenantId of projectIds) {
			try {
				await ownerApiCall(
					`/v3/organizations/${encodeURIComponent(organizationId)}/tenants/${encodeURIComponent(tenantId)}/activate`,
					{ method: 'POST' },
				)
				activatedCount++
			} catch (error) {
				console.error(`Failed to activate project ${tenantId}:`, error)
				throw error
			}
		}
		return {
			success: activatedCount === projectIds.length,
			activatedCount,
			message: `Activated ${activatedCount} of ${projectIds.length} projects`,
		}
	},
	// Note: Backend currently supports single tenant deactivation; looping for bulk action.
	deactivateProjects: async (organizationId: string, projectIds: string[]): Promise<DeactivateProjectsResponse> => {
		let deactivatedCount = 0
		for (const tenantId of projectIds) {
			try {
				await ownerApiCall(
					`/v3/organizations/${encodeURIComponent(organizationId)}/tenants/${encodeURIComponent(tenantId)}/deactivate`,
					{ method: 'POST' },
				)
				deactivatedCount++
			} catch (error) {
				console.error(`Failed to deactivate project ${tenantId}:`, error)
				throw error
			}
		}
		return {
			success: deactivatedCount === projectIds.length,
			deactivatedCount,
			message: `Deactivated ${deactivatedCount} of ${projectIds.length} projects`,
		}
	},
	// Note: Backend currently supports single tenant deletion; looping for bulk action.
	deleteProjects: async (organizationId: string, projectIds: string[]): Promise<DeleteProjectsResponse> => {
		let deletedCount = 0
		for (const tenantId of projectIds) {
			try {
				await ownerApiCall(
					`/v3/organizations/${encodeURIComponent(organizationId)}/tenants/${encodeURIComponent(tenantId)}/delete`,
					{
						method: 'POST',
					},
				)
				deletedCount++
			} catch (error) {
				console.error(`Failed to delete project ${tenantId}:`, error)
				throw error
			}
		}
		return {
			success: deletedCount === projectIds.length,
			deletedCount,
			message: `Deleted ${deletedCount} of ${projectIds.length} projects`,
		}
	},
	/** Lightweight project name lookup — no enrichment, no pagination. */
	listProjectNames: async (organizationId: string): Promise<Map<string, string>> => {
		try {
			const response = await ownerApiCall<{ tenants: ProjectAPIResponse[] }>(
				`/v3/organizations/${encodeURIComponent(organizationId)}/tenants`,
			)
			return new Map(response.tenants.map(t => [t.id, t.name || t.id]))
		} catch (error) {
			console.error('Failed to fetch project names:', error)
			return new Map()
		}
	},
	updateProjectFrontendSettings: async (tenantId: string, frontendSettings: Record<string, unknown>): Promise<void> => {
		await patchTenantSettings(tenantId, { frontend_settings: frontendSettings })
	},
}
